import React, { Component, useEffect, useMemo, useRef, useState } from "react";
import * as LeafletNS from "leaflet";
import "leaflet/dist/leaflet.css";
import { isPrerendering } from "../utils/isPrerendering";
import {
  magHqSite,
  mapLayers,
  partnerMapSites,
  plantSites,
} from "../data/mapSites";

const L = LeafletNS.default || LeafletNS;

// OSM now 403s tiles when Referer is missing. Production sends
// Referrer-Policy: same-origin (Cloudflare), so Leaflet's default
// <img> requests never include one. Set it on the tile before src.
const OsmTileLayer = L.TileLayer.extend({
  createTile(coords, done) {
    const tile = document.createElement("img");
    L.DomEvent.on(tile, "load", L.bind(this._tileOnLoad, this, done, tile));
    L.DomEvent.on(tile, "error", L.bind(this._tileOnError, this, done, tile));
    tile.alt = "";
    tile.referrerPolicy = "strict-origin-when-cross-origin";
    tile.src = this.getTileUrl(coords);
    return tile;
  },
});

const PIN = {
  hq: { size: 28, className: "mag-map-pin mag-map-pin-hq" },
  plants: { size: 22, className: "mag-map-pin mag-map-pin-plant" },
  partners: { size: 18, className: "mag-map-pin mag-map-pin-partner" },
};

const CLUSTER_PIXELS = 44;

function sitesForLayers(active) {
  const sites = [];
  if (active.hq) sites.push(magHqSite);
  if (active.plants) sites.push(...plantSites);
  if (active.partners) sites.push(...partnerMapSites());
  return sites;
}

function toRad(value) {
  return (value * Math.PI) / 180;
}

function toDeg(value) {
  return (value * 180) / Math.PI;
}

function shortestLonDelta(fromLng, toLng) {
  let delta = toLng - fromLng;
  while (delta > 180) delta -= 360;
  while (delta < -180) delta += 360;
  return delta;
}

function wrapsWestAcrossPacific(from, to) {
  return shortestLonDelta(from.lng, to.lng) < 0 && to.lng - from.lng > 180;
}

function greatCircleLatLngs(from, to) {
  const lat1 = toRad(from.lat);
  const lon1 = toRad(from.lng);
  const lat2 = toRad(to.lat);
  const lon2 = toRad(to.lng);
  const d = 2 * Math.asin(
    Math.min(
      1,
      Math.sqrt(
        Math.sin((lat2 - lat1) / 2) ** 2 +
          Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2
      )
    )
  );

  if (!Number.isFinite(d) || d < 1e-6) {
    return [
      [from.lat, from.lng],
      [to.lat, to.lng],
    ];
  }

  const steps = Math.max(12, Math.min(72, Math.round(d * 28)));
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const f = i / steps;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
    const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
    const z = A * Math.sin(lat1) + B * Math.sin(lat2);
    points.push([toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))), toDeg(Math.atan2(y, x))]);
  }
  return points;
}

function eastwardArcLatLngs(from, to) {
  let eastLng = to.lng;
  while (eastLng <= from.lng) eastLng += 360;
  const span = eastLng - from.lng;
  const steps = Math.max(28, Math.min(80, Math.round((span * Math.PI * 18) / 180)));
  const bulge = 10;
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    points.push([
      from.lat + (to.lat - from.lat) * t + Math.sin(t * Math.PI) * bulge,
      from.lng + span * t,
    ]);
  }
  return points;
}

function routeLatLngs(from, to) {
  return wrapsWestAcrossPacific(from, to) ? eastwardArcLatLngs(from, to) : greatCircleLatLngs(from, to);
}

function polylineSegments(points) {
  const segments = [[]];
  points.forEach((point) => {
    const current = segments[segments.length - 1];
    if (current.length) {
      const prev = current[current.length - 1];
      if (Math.abs(point[1] - prev[1]) > 180) {
        segments.push([point]);
        return;
      }
    }
    current.push(point);
  });
  return segments.filter((segment) => segment.length > 1);
}
function formatCoord(value, axis) {
  const abs = Math.abs(value).toFixed(2);
  const dir = axis === "lat" ? (value >= 0 ? "N" : "S") : value >= 0 ? "E" : "W";
  return `${abs}° ${dir}`;
}

function clusterOverlapping(sites, map) {
  if (!map || !sites.length) return [];
  try {
    if (!map.getSize() || map.getSize().x === 0) {
      return sites.map((site) => [site]);
    }
  } catch {
    return sites.map((site) => [site]);
  }

  const placed = sites.map((site) => ({
    site,
    point: map.latLngToLayerPoint(L.latLng(site.lat, site.lng)),
  }));
  const used = new Set();
  const groups = [];

  placed.forEach((item, index) => {
    if (used.has(index)) return;
    const members = [item.site];
    used.add(index);
    placed.forEach((other, otherIndex) => {
      if (used.has(otherIndex)) return;
      const dx = item.point.x - other.point.x;
      const dy = item.point.y - other.point.y;
      if (Math.hypot(dx, dy) <= CLUSTER_PIXELS) {
        members.push(other.site);
        used.add(otherIndex);
      }
    });
    groups.push(members);
  });

  return groups;
}

function clusterLayer(members) {
  if (members.some((site) => site.layer === "hq")) return "hq";
  if (members.some((site) => site.layer === "plants")) return "plants";
  return "partners";
}

function sitePopup(site) {
  const hud = document.createElement("div");
  hud.className = "mag-map-hud";

  const kicker = document.createElement("p");
  kicker.className =
    site.layer === "plants"
      ? "mag-map-hud-kicker mag-map-hud-kicker-plant"
      : "mag-map-hud-kicker";
  kicker.textContent =
    site.layer === "hq" ? "Headquarters" : site.layer === "plants" ? "Plant" : "Partner";

  const title = document.createElement("p");
  title.className = "mag-map-hud-title";
  title.textContent = site.name;

  const detail = document.createElement("p");
  detail.className = "mag-map-hud-detail";
  detail.textContent = site.detail;

  const coord = document.createElement("p");
  coord.className = "mag-map-hud-coord";
  coord.textContent = `${formatCoord(site.lat, "lat")}  ${formatCoord(site.lng, "lng")}`;

  hud.append(kicker, title, detail, coord);

  if (site.href) {
    const link = document.createElement("a");
    link.href = site.href;
    link.className = "mag-map-hud-link";
    link.textContent = "Partner page";
    hud.append(link);
  }

  return hud;
}

function setMapView(map, sites) {
  if (!sites.length) {
    map.setView([magHqSite.lat, magHqSite.lng], 5, { animate: false });
    return;
  }
  if (sites.length === 1) {
    map.setView([sites[0].lat, sites[0].lng], 8, { animate: false });
    return;
  }
  const bounds = L.latLngBounds(sites.map((site) => [site.lat, site.lng]));
  const overseas = sites.some((site) => site.lng > -20);
  map.fitBounds(bounds, { padding: [56, 56], maxZoom: overseas ? 4 : 8, animate: false });
}

class MapErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <p className="px-4 py-16 text-center text-sm text-white/60">
          Map could not load. MAG HQ is in Livonia, Michigan.
        </p>
      );
    }
    return this.props.children;
  }
}

function wheelDelta(event) {
  const line = 16;
  const page = window.innerHeight;
  let x = event.deltaX;
  let y = event.deltaY;
  if (event.deltaMode === 1) {
    x *= line;
    y *= line;
  } else if (event.deltaMode === 2) {
    x *= page;
    y *= page;
  }
  return { x, y };
}

function MagPresenceMapInner() {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef(null);
  const sitesRef = useRef([]);
  const [ready, setReady] = useState(false);
  const [active, setActive] = useState({
    hq: true,
    partners: true,
    plants: true,
  });

  const visibleSites = useMemo(() => sitesForLayers(active), [active]);
  const counts = {
    hq: 1,
    partners: partnerMapSites().length,
    plants: plantSites.length,
  };

  useEffect(() => {
    sitesRef.current = visibleSites;
  }, [visibleSites]);

  useEffect(() => {
    const node = mapNodeRef.current;
    if (!node || isPrerendering() || typeof L.map !== "function") return undefined;
    if (node._leaflet_id) {
      node._leaflet_id = null;
    }

    let map;
    let mapEl;
    let enableWheelZoom;
    let disableWheelZoom;
    let onDocumentPointerDown;
    let onMapWheel;
    try {
      map = L.map(node, {
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true,
        minZoom: 2,
        maxZoom: 12,
        worldCopyJump: true,
        center: [magHqSite.lat, magHqSite.lng],
        zoom: 5,
      });

      new OsmTileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);

      map.zoomControl.setPosition("topright");
      layersRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;

      mapEl = map.getContainer();
      enableWheelZoom = () => {
        map.scrollWheelZoom.enable();
        mapEl.style.overscrollBehavior = "contain";
      };
      disableWheelZoom = () => {
        map.scrollWheelZoom.disable();
        mapEl.style.overscrollBehavior = "auto";
      };
      onDocumentPointerDown = (event) => {
        if (mapEl.contains(event.target)) {
          enableWheelZoom();
        } else {
          disableWheelZoom();
        }
      };
      onMapWheel = (event) => {
        if (map.scrollWheelZoom.enabled()) return;
        event.preventDefault();
        const { x, y } = wheelDelta(event);
        window.scrollBy(x, y);
      };

      mapEl.addEventListener("wheel", onMapWheel, { passive: false });
      mapEl.addEventListener("mouseleave", disableWheelZoom);
      document.addEventListener("pointerdown", onDocumentPointerDown);
      map.whenReady(() => setReady(true));
    } catch {
      return undefined;
    }

    return () => {
      if (mapEl && onMapWheel) {
        mapEl.removeEventListener("wheel", onMapWheel);
      }
      if (mapEl && disableWheelZoom) {
        mapEl.removeEventListener("mouseleave", disableWheelZoom);
      }
      if (onDocumentPointerDown) {
        document.removeEventListener("pointerdown", onDocumentPointerDown);
      }
      if (map) {
        map.remove();
      }
      mapRef.current = null;
      layersRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const group = layersRef.current;
    if (!ready || !map || !group) return undefined;

    const addCityLabel = (site) => {
      const text = site.label || site.name;
      const offset = site.labelOffset || { x: 16, y: 0 };
      const marker = L.marker([site.lat, site.lng], {
        icon: L.divIcon({
          className: `mag-map-city-label mag-map-city-label-${site.layer}`,
          html: `<span class="mag-map-city-label-text" style="transform: translate(${offset.x}px, ${offset.y}px)">${text}</span>`,
          iconSize: [1, 1],
          iconAnchor: [0, 0],
        }),
        interactive: false,
        keyboard: false,
        zIndexOffset: 50,
      });
      group.addLayer(marker);
    };

    const addHqLine = (site) => {
      const isPlant = site.layer === "plants";
      const color = isPlant ? "#3b82f6" : "#10b981";
      const className = isPlant ? "mag-map-link mag-map-link-plant" : "mag-map-link mag-map-link-partner";
      polylineSegments(routeLatLngs(magHqSite, site)).forEach((segment) => {
        L.polyline(segment, {
          className,
          color,
          weight: isPlant ? 1.35 : 1.15,
          opacity: 0.22,
          interactive: false,
        }).addTo(group);
        L.polyline(segment, {
          className: `${className} mag-map-link-flow`,
          color,
          weight: isPlant ? 1.7 : 1.5,
          opacity: 0.9,
          dashArray: "10 22",
          lineCap: "round",
          interactive: false,
        }).addTo(group);
      });
    };

    const addSiteMarker = (site) => {
      const pin = PIN[site.layer];
      const marker = L.marker([site.lat, site.lng], {
        icon: L.divIcon({
          className: pin.className,
          html: '<span class="mag-map-pin-ring"></span><span class="mag-map-pin-core"></span>',
          iconSize: [pin.size, pin.size],
          iconAnchor: [pin.size / 2, pin.size / 2],
        }),
        title: `${site.name}. ${site.detail}`,
        keyboard: true,
        zIndexOffset: site.layer === "hq" ? 400 : 200,
      });
      marker.bindPopup(sitePopup(site), {
        closeButton: false,
        offset: [0, -12],
        className: "mag-map-hud-popup",
      });
      group.addLayer(marker);
    };

    const drawPins = () => {
      try {
        group.clearLayers();
        const sites = sitesRef.current;
        sites
          .filter((site) => site.layer !== "hq")
          .forEach(addHqLine);
        sites.forEach(addCityLabel);

        clusterOverlapping(sites, map).forEach((members) => {
          if (members.length === 1) {
            addSiteMarker(members[0]);
            return;
          }

          const layer = clusterLayer(members);
          const pin = PIN[layer];
          const lat = members.reduce((sum, site) => sum + site.lat, 0) / members.length;
          const lng = members.reduce((sum, site) => sum + site.lng, 0) / members.length;
          const marker = L.marker([lat, lng], {
            icon: L.divIcon({
              className: `${pin.className} mag-map-pin-cluster`,
              html: `<span class="mag-map-pin-ring"></span><span class="mag-map-pin-core"></span><span class="mag-map-pin-count">${members.length}</span>`,
              iconSize: [pin.size, pin.size],
              iconAnchor: [pin.size / 2, pin.size / 2],
            }),
            title: `${members.length} locations`,
            keyboard: true,
            zIndexOffset: 500,
          });
          marker.on("click", () => {
            const bounds = L.latLngBounds(members.map((site) => [site.lat, site.lng]));
            map.fitBounds(bounds, { padding: [48, 48], maxZoom: 11, animate: true });
          });
          group.addLayer(marker);
        });
      } catch {
        /* keep the rest of About usable if clustering fails */
      }
    };

    setMapView(map, visibleSites);
    drawPins();
    map.on("zoomend", drawPins);

    return () => {
      map.off("zoomend", drawPins);
    };
  }, [ready, visibleSites]);

  const toggleLayer = (id) => {
    setActive((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (!next.hq && !next.partners && !next.plants) {
        return prev;
      }
      return next;
    });
  };

  return (
    <section className="bg-gray-950 text-white" aria-labelledby="presence-map-heading">
      <div className="px-4 pt-16 sm:px-6 sm:pt-20 lg:px-8">
        <p className="page-eyebrow-on-dark">Where MAG works</p>
        <h2 id="presence-map-heading" className="mt-3 text-2xl font-bold tracking-tight sm:text-4xl">
          HQ, partners, and plants
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
          MAG HQ is in Metro Detroit. Toggle partners to see vendor headquarters. Plants are the floors
          where MAG has robots running.
        </p>
      </div>

      <div className="relative mt-10">
        <div className="mag-presence-map">
          <div className="mag-presence-map-frame" aria-hidden="true" />
          <div
            ref={mapNodeRef}
            className="h-[22rem] w-full bg-gray-200 sm:h-[32rem] lg:h-[40rem]"
            role="img"
            aria-label="Map of MAG headquarters, partner headquarters, and plant sites"
          />
          {!ready ? (
            <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-white/50">
              MAG HQ, Livonia, Michigan
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-6 px-4 py-6 sm:flex-row sm:items-end sm:justify-between sm:px-6 lg:px-8">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Map layers">
          {mapLayers.map((layer) => {
            const on = active[layer.id];
            return (
              <button
                key={layer.id}
                type="button"
                aria-pressed={on}
                onClick={() => toggleLayer(layer.id)}
                className={
                  on
                    ? "border border-white bg-white px-3 py-2 text-xs font-semibold tracking-wide text-gray-900"
                    : "border border-white/30 bg-transparent px-3 py-2 text-xs font-semibold tracking-wide text-white/80 hover:border-white"
                }
              >
                {layer.label}
              </button>
            );
          })}
        </div>
        <ul className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-white/80">
          <li>
            <span className="text-2xl font-bold tabular-nums text-white">{counts.hq}</span>
            <span className="ml-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/45">
              MAG HQ
            </span>
          </li>
          <li>
            <span className="text-2xl font-bold tabular-nums text-white">{counts.partners}</span>
            <span className="ml-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/45">
              Partners
            </span>
          </li>
          <li>
            <span className="text-2xl font-bold tabular-nums text-white">{counts.plants}</span>
            <span className="ml-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/45">
              Plants
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}

export default function MagPresenceMap() {
  return (
    <MapErrorBoundary>
      <MagPresenceMapInner />
    </MapErrorBoundary>
  );
}
